/**
 * CAI section markers — used to embed generated content in user-owned files.
 * Content outside the markers is never touched.
 */
export const CAI_START = "<!-- cai:start -->";
export const CAI_END = "<!-- cai:end -->";

/**
 * Merge generated content into an existing file.
 *
 * - If the file doesn't exist: write CAI content wrapped in markers.
 * - If markers exist: replace only the content between them.
 * - If no markers exist: append the CAI block at the end (first run migration).
 *
 * Returns the merged string (caller decides whether to write).
 * Throws if markers are malformed (duplicate, nested, or reversed).
 */
export function mergeWithMarkers(existing: string | null, generated: string): string {
  const block = `${CAI_START}\n${generated.trimEnd()}\n${CAI_END}`;

  if (!existing) {
    return block + "\n";
  }

  const startCount = occurrences(existing, CAI_START);
  const endCount = occurrences(existing, CAI_END);

  if (startCount > 1 || endCount > 1) {
    throw new Error(
      `File contains duplicate CAI markers (${startCount} start, ${endCount} end). ` +
      `Remove extra "${CAI_START}" / "${CAI_END}" markers and retry.`
    );
  }

  const startIdx = existing.indexOf(CAI_START);
  const endIdx = existing.indexOf(CAI_END);

  if ((startIdx !== -1) !== (endIdx !== -1)) {
    throw new Error(
      `File contains an unpaired CAI marker. ` +
      `Both "${CAI_START}" and "${CAI_END}" must be present, or neither.`
    );
  }

  if (startIdx !== -1 && endIdx !== -1 && endIdx <= startIdx) {
    throw new Error(
      `CAI markers are in wrong order — "${CAI_END}" appears before "${CAI_START}".`
    );
  }

  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    // Replace existing block
    return (
      existing.slice(0, startIdx) +
      block +
      existing.slice(endIdx + CAI_END.length)
    );
  }

  // No markers yet — append block, preserving existing user content
  const trimmed = existing.trimEnd();
  return trimmed ? `${trimmed}\n\n${block}\n` : `${block}\n`;
}

function occurrences(text: string, marker: string): number {
  let count = 0;
  let idx = 0;
  while ((idx = text.indexOf(marker, idx)) !== -1) {
    count++;
    idx += marker.length;
  }
  return count;
}

/** Extract only the CAI-managed section from a file (for diffing) */
export function extractCaiSection(content: string): string | null {
  const startIdx = content.indexOf(CAI_START);
  const endIdx = content.indexOf(CAI_END);
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return null;
  return content.slice(startIdx + CAI_START.length, endIdx).trim();
}
